import {useRouter} from 'next/router';
import {Box, List, ListItem, ListItemText} from '@material-ui/core';
import {css} from '@emotion/react';

import {Season} from 'types/seasons';

import { getUTCDateWithFormat} from 'lib/dates';
import {BLANK_CHARACTER} from 'lib/characters';

import {useColors} from 'hooks/useColors';
import {IColors} from 'types/colors';
import {flexColCss, lineClampCss, setOpacity} from 'styles/utils';
import {Product} from 'types/product';
import {i18n} from 'lib/i18n';
import {Pricify} from '@bondsports/utils';
import {getResourceSettingValueByKey} from '../../../lib/resourceSettings';
import {ESessionResourceSettingKey} from '../../../types/resource-settings';
import {EDateFormats} from "types/times";

const boxItemCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};
	cursor: pointer;
	padding: 3rem;
	height: 300px;
	min-width: 280px;
`;

const listItemCss = (colors: IColors) => css`
	height: 100%;
	${flexColCss};
	justify-content: space-evenly;
	li {
		padding: 0;
	}

	li .MuiListItemText-primary {
		color: ${setOpacity(colors.tx_text_primary || '', 0.37)};
		font-size: 1rem;
		max-height: 5rem;
	}

	li .MuiListItemText-secondary {
		font-size: 1.6rem;
		color: ${colors.tx_text_primary};
		font-weight: 600;
	}
`;

const upperCaseTitleCss = css`
	text-transform: uppercase;
`;

const seasonNameCss = (colors: IColors) => css`
	${lineClampCss(2)};
	${upperCaseTitleCss};
	min-height: 40px;
	font-size: 1.8rem;
	line-height: 2rem;
	font-weight: bold;
	color: ${colors.tx_text_primary};
	max-width: 100%;
	margin: 0;
`;

const seasonPriceCss = (colors: IColors) => css`
	span.MuiListItemText-primary {
		font-size: 2.8rem;
		color: ${colors.tx_system};
		line-height: 2.2rem;
		font-weight: bold;
	}
`;

const convertDate = (seasonDate: string) => {
	const [date, ] = seasonDate.split('T');
	const [, month, day] = date.split('-');

	if (!month || !day) {
		return BLANK_CHARACTER;
	}

	return getUTCDateWithFormat(seasonDate, EDateFormats.MMM_DD);
};

const getSeasonDatesRange = (startDate: string, endDate: string) => {
	return `${convertDate(startDate)} - ${convertDate(endDate)}`;
};

const getPrice = (products: Product[]) => {
	if (
		!products ||
		!products.length ||
		!Array.isArray(products) ||
		!products[0].prices ||
		!Array.isArray(products[0].prices) ||
		!products[0].prices.length
	) {
		return BLANK_CHARACTER;
	}

	const price = products[0].defaultPrice?.price || 0;
	return `${Pricify(price)}`;

}

const SeasonDetails = (props: Season) => {
	const { id, name, registrationStartDate, startDate, endDate, products, resourceSettings } = props;
	const router = useRouter();
	const { programId, programSeoName } = router.query;
	const { colors } = useColors();

	const isHideEventTimes =
		getResourceSettingValueByKey(resourceSettings ?? [], ESessionResourceSettingKey.HIDE_EVENT_TIMES) === 'true';
	const clickSeasonHandler = () => {
		const seasonSeoName: string = name.toLowerCase().split(' ').join('-');

		router.push(`/activity/programs/${programSeoName}/${programId}/season/${encodeURIComponent(seasonSeoName)}/${id}`);
	};

	return (
		<Box css={boxItemCss(colors)} onClick={clickSeasonHandler}>
			<h3  data-testid="SeasonDetails-EF514D" css={seasonNameCss(colors)}>{name}</h3>
			<List css={listItemCss(colors)}>
				<ListItem>
					<ListItemText
						secondary={registrationStartDate ? convertDate(registrationStartDate) : BLANK_CHARACTER}
						primary={i18n.programs.registration.starts}
					/>
				</ListItem>
				<ListItem>
					{isHideEventTimes ? (
						<ListItemText secondary={BLANK_CHARACTER} primary={BLANK_CHARACTER} />
					) : (
						<ListItemText
							secondary={startDate && endDate ? getSeasonDatesRange(startDate, endDate) : BLANK_CHARACTER}
							primary="Dates"
						/>
					)}
				</ListItem>
				<ListItem>
					<ListItemText css={seasonPriceCss(colors)}>{getPrice((products as Product[]) || [])}</ListItemText>
				</ListItem>
			</List>
		</Box>
	);
};

export default SeasonDetails;
